import { call, delay, put, race, select, take } from 'typed-redux-saga/macro';

import { getRoute } from 'Config';
import { getInstance as getConfigServiceInstance } from 'Lib/ConfigService';
import { LEVER_EXPLORE_IS_AVAILABLE, LEVER_PREFERENCES_IS_ENABLED } from 'Lib/ConfigService/levers';
import { ONE_SECOND } from 'Lib/constants/time';
import { GetActionByType } from 'Lib/reduxType';
import { getSearchParams } from 'Lib/Session';

import { getIsProfileLoaded } from 'Store/app/selectors';
import { getIsTinderPlusEnabled } from 'Store/appSettings/selectors';
import { setUserTokenError, setUserTokenSuccess } from 'Store/emailSubscriptionManagement/actions';
import { setIncentivePromoSource } from 'Store/incentives/actions';
import { verifyRouteSuccess } from 'Store/loading/actions';
import { getDenormalizedMatchById } from 'Store/match/selectors';
import { FETCH_META_ERROR, FETCH_META_SUCCESS } from 'Store/meta/constants';
import { openModal } from 'Store/modals/actions';
import { MODAL_LOGIN } from 'Store/modals/constants';
import { isMutualFriendsEnabledAndSupported } from 'Store/mutualFriends/helpers';
import { FETCH_PROFILE_SUCCESS } from 'Store/profile/constants';
import { getHasPremiumSubscription, getHasSubscription } from 'Store/profile/revenue.selectors';
import { getCurrentUser, selectUserHasUnlimitedPreferencesAccess } from 'Store/profile/selectors';
import { navigate } from 'Store/routing/actions';
import { fetchMatch } from 'Store/updates/actions';
import {
  FETCH_INITIAL_MATCHES_SUCCESS,
  FETCH_MATCH_FAILURE,
  FETCH_MATCH_SUCCESS,
} from 'Store/updates/constants';
import {
  setDeeplinkIncentivesInfo,
  setDeeplinkToPromo,
  setDeeplinkVerificationInfo,
  setGoldDeeplinkActive,
} from 'Store/user/actions';
import { getIsUserLoggedIn } from 'Store/user/selectors';

import { MODAL_OPEN_DELAY as LOGIN_MODAL_OPEN_DELAY } from 'Containers/LoginPage/constants';

import { handleInitialRouting } from './deeplink/common';
import {
  VERIFY_AGE_VERIFICATION_DEEPLINK_ROUTE,
  VERIFY_INCENTIVES_DEEPLINK_ROUTE,
  VERIFY_MESSAGES_ROUTE,
  VERIFY_MUTUAL_FRIENDS_ROUTE,
  VERIFY_WEB_PROFILE_ROUTE,
} from './constants';

// NOTE: verifyRouteSuccess must be called at the end of every route verification saga
/**
 * Selectors
 */

/**
 * Sagas
 */
const ROUTE_TIMEOUT = 5 * ONE_SECOND;

export function* handleLoginRouteSaga() {
  yield* put(navigate(getRoute('login', { pathConfig: { withPrefix: false } }), { replace: true }));
  yield* put(verifyRouteSuccess());

  const userLoggedIn = yield* select(getIsUserLoggedIn);

  if (!userLoggedIn) {
    yield* delay(LOGIN_MODAL_OPEN_DELAY);
    yield* put(openModal(MODAL_LOGIN));
  }
}

function* handleUnAuth() {
  const userLoggedIn = yield* select(getIsUserLoggedIn);
  if (!userLoggedIn) {
    yield* call(handleLoginRouteSaga);
  }
  return userLoggedIn;
}

function* fetchMatchById(id) {
  yield* take([FETCH_INITIAL_MATCHES_SUCCESS]);
  const match = yield* select((state) => getDenormalizedMatchById(state, id));
  if (!match) {
    yield* put(fetchMatch(id));
    const payload = yield* take([FETCH_MATCH_SUCCESS, FETCH_MATCH_FAILURE]);
    return payload?.type;
  }
}

export function* verifyMessagesRouteSaga(action: GetActionByType<typeof VERIFY_MESSAGES_ROUTE>) {
  const match = yield* select((state) => getDenormalizedMatchById(state, action.payload.matchId));
  const userLoggedIn = yield* call(handleUnAuth);
  if (!userLoggedIn) {
    return;
  }

  if (!match) {
    const { fetchMatchCompleted, timeout } = yield* race({
      fetchMatchCompleted: call(fetchMatchById, action.payload.matchId),
      timeout: delay(ROUTE_TIMEOUT),
    });

    if (timeout || fetchMatchCompleted === FETCH_MATCH_FAILURE) {
      yield* put(navigate(getRoute('matches'), { replace: true }));
    }
  }
  yield* put(verifyRouteSuccess());
}

export function* verifyMutualFriendsRouteSaga(
  _: GetActionByType<typeof VERIFY_MUTUAL_FRIENDS_ROUTE>
) {
  if (!isMutualFriendsEnabledAndSupported()) {
    yield* put(navigate(getRoute('settings'), { replace: true }));
    return;
  }

  yield* put(verifyRouteSuccess());
}

export function* verifySettingsRouteSaga() {
  const userLoggedIn = yield* call(handleUnAuth);
  if (!userLoggedIn) {
    return;
  }
  const { timeout } = yield* race({
    profileLoaded: call(function* verifyProfileLoaded() {
      yield* take([FETCH_PROFILE_SUCCESS]);
      const isProfileLoaded = yield* select(getIsProfileLoaded);
      if (!isProfileLoaded) {
        yield* put(navigate(getRoute('settings'), { replace: true }));
      }
    }),
    timeout: delay(ROUTE_TIMEOUT),
  });

  if (timeout) {
    yield* put(navigate(getRoute('settings'), { replace: true }));
  }

  yield* put(verifyRouteSuccess());
}

export function* verifyPlusRouteSaga() {
  const userLoggedIn = yield* call(handleUnAuth);
  if (!userLoggedIn) {
    return;
  }
  const { timeout } = yield* race({
    plusEnabled: call(function* verifyPlusEnabled() {
      yield* take([FETCH_META_SUCCESS, FETCH_META_ERROR]);
      const isTinderPlusEnabled = yield* select(getIsTinderPlusEnabled);
      if (!isTinderPlusEnabled) {
        yield* put(navigate(getRoute('recs'), { replace: true }));
      }

      if (!(yield* select(getHasSubscription))) {
        yield* put(navigate(getRoute('recs'), { replace: true }));
      }
    }),
    timeout: delay(ROUTE_TIMEOUT),
  });

  if (timeout) {
    yield* put(navigate(getRoute('recs'), { replace: true }));
  }

  yield* put(verifyRouteSuccess());
}

export function* verifyMerchandisingRouteSaga() {
  const userLoggedIn = yield* call(handleUnAuth);
  if (!userLoggedIn) {
    return;
  }
  const { timeout } = yield* race({
    plusEnabled: call(function* verifyPlusEnabled() {
      yield* take([FETCH_META_SUCCESS, FETCH_META_ERROR, FETCH_PROFILE_SUCCESS]);
      const isTinderPlusEnabled = yield* select(getIsTinderPlusEnabled);
      if (!isTinderPlusEnabled) {
        yield* put(navigate(getRoute('recs'), { replace: true }));
      }
    }),
    timeout: delay(ROUTE_TIMEOUT),
  });

  if (timeout) {
    yield* put(navigate(getRoute('recs'), { replace: true }));
  }

  yield* put(verifyRouteSuccess());
}

export function* verifyGoldRouteSaga() {
  const { timeout } = yield* race({
    profileLoaded: call(function* verifyProfileLoaded() {
      yield* take([FETCH_PROFILE_SUCCESS]);
      const hasPremiumSubscription = yield* select(getHasPremiumSubscription);
      if (!hasPremiumSubscription) {
        yield* put(navigate(getRoute('recs'), { replace: true }));
      }
    }),
    timeout: delay(ROUTE_TIMEOUT),
  });

  if (timeout) {
    yield* put(navigate(getRoute('recs'), { replace: true }));
  }
  yield* put(verifyRouteSuccess());
}

export function* verifyPreferencesRouteSaga() {
  const userLoggedIn = yield* call(handleUnAuth);
  if (!userLoggedIn) {
    return;
  }
  const { timeout } = yield* race({
    preferencesEnabled: call(function* verifyPreferencesEnabled() {
      yield* take([FETCH_META_SUCCESS, FETCH_META_ERROR, FETCH_PROFILE_SUCCESS]);
      const isProfileLoaded = yield* select(getIsProfileLoaded);
      const isTinderPlusEnabled = yield* select(getIsTinderPlusEnabled);
      if (!isTinderPlusEnabled || !isProfileLoaded) {
        yield* put(navigate(getRoute('settings'), { replace: true }));
      }

      const hasPremiumSubscription = yield* select(getHasPremiumSubscription);
      const configService = getConfigServiceInstance();
      const isPreferencesEnabled = configService.readLever(LEVER_PREFERENCES_IS_ENABLED);
      const hasUnlimitedPreferencesAccess = yield* select(selectUserHasUnlimitedPreferencesAccess);
      if (!((hasPremiumSubscription && isPreferencesEnabled) || hasUnlimitedPreferencesAccess)) {
        yield* put(navigate(getRoute('settings'), { replace: true }));
        // COMMENTED OUT - logic for free and plus test for preferences
        // const subName = yield* select(getPurchasedSubscriptionName);
        // const requiredTier = 'gold';
        // if (subName !== requiredTier) {
        //   yield* put(navigate(getRoute('settings'), { replace: true }));
        // }
      }
    }),
    timeout: delay(ROUTE_TIMEOUT),
  });

  if (timeout) {
    yield* put(navigate(getRoute('settings'), { replace: true }));
  }

  yield* put(verifyRouteSuccess());
}

export function* verifyAgeVerificationDeeplinkRouteSaga(
  action: GetActionByType<typeof VERIFY_AGE_VERIFICATION_DEEPLINK_ROUTE>
) {
  const { status } = action.payload;
  yield* put(
    setDeeplinkVerificationInfo({
      type: 'age-verification',
      otp: status,
    })
  );
  yield* call(handleInitialRouting);
}

export function* verifyGoldDeeplinkRouteSaga() {
  yield* put(setGoldDeeplinkActive());
  yield* call(handleInitialRouting);
}

export function* verifyEmailSubscriptionTokenRouteSaga() {
  const params = __CLIENT__ ? getSearchParams(window.location.search) : null;
  const token = params?.get('token');
  if (!token) {
    yield* put(setUserTokenError());
  } else {
    yield* put(setUserTokenSuccess(token));
  }
  yield* put(verifyRouteSuccess());
}

export function* verifyIncentivesRouteSaga(
  action: GetActionByType<typeof VERIFY_INCENTIVES_DEEPLINK_ROUTE>
) {
  yield* put(setDeeplinkIncentivesInfo(action.payload));
  yield* put(setIncentivePromoSource('url'));
  yield* call(handleInitialRouting);
}

export function* verifyPromoRouteSaga() {
  yield* put(setDeeplinkToPromo());
  yield* put(setIncentivePromoSource('url'));
  yield* call(handleInitialRouting);
}

export function* verifyWebProfileRouteSaga(
  action: GetActionByType<typeof VERIFY_WEB_PROFILE_ROUTE>
) {
  const { username: requestedUsername } = action.payload;

  const userLoggedIn = yield* select(getIsUserLoggedIn);
  if (userLoggedIn) {
    let user = yield* select(getCurrentUser);

    // Waiting if user data hasn't been fetched yet
    if (!user._id) {
      yield* take(FETCH_PROFILE_SUCCESS);
      user = yield* select(getCurrentUser);
    }

    if (requestedUsername === user.username) {
      yield* put(navigate(getRoute('profile'), { replace: true }));
    } else {
      yield* put(navigate(getRoute('recs'), { replace: true }));
    }
  }

  yield* put(verifyRouteSuccess());
}

export function* verifyExploreRouteSaga() {
  const configService = getConfigServiceInstance();
  const isExploreAvailable = configService.readLever(LEVER_EXPLORE_IS_AVAILABLE);

  if (!isExploreAvailable) {
    yield* put(navigate(getRoute('recs'), { replace: true }));
  }
  yield* put(verifyRouteSuccess());
}
